/**
 * Phone verification service.
 *
 * Bridges the API layer and the UI the same way authService does, to avoid a circular
 * dependency between `api/index.js` and the component tree.
 *
 * Why this exists rather than a check on the user record: the API decides whether a phone
 * must be verified from conditions the frontend cannot see — whether the team is a booking
 * referrer, whether SSO is required for the email, and whether the user has ever submitted
 * an inquiry before (see EnsurePhoneVerifiedForInquiryDispatch). Re-deriving those here
 * would drift, and guessing wrong in the permissive direction leaves users stuck while
 * guessing wrong in the strict direction blocks people who were never gated. Reacting to
 * the API's own 403 is right by construction.
 */

type PhoneVerificationRequiredHandler = () => void;

let handlers: PhoneVerificationRequiredHandler[] = [];

export const registerPhoneVerificationRequiredHandler = (handler: PhoneVerificationRequiredHandler): void => {
    handlers.push(handler);
};

export const unregisterPhoneVerificationRequiredHandler = (handler: PhoneVerificationRequiredHandler): void => {
    const index = handlers.indexOf(handler);
    if (index > -1) {
        handlers.splice(index, 1);
    }
};

/** Called when an API request is refused because the user's phone is unverified. */
export const triggerPhoneVerificationRequired = (): void => {
    handlers.forEach(handler => {
        try {
            handler();
        } catch (error) {
            console.error('Error in phone verification handler:', error);
        }
    });
};

/** Clear all registered handlers (useful for testing) */
export const clearHandlers = (): void => {
    handlers = [];
};

export const phoneVerificationService = {
    registerPhoneVerificationRequiredHandler,
    unregisterPhoneVerificationRequiredHandler,
    triggerPhoneVerificationRequired,
    clearHandlers,
};
